// Support — contact-form triage. Replaces the FormsInbox CMS panel.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { PageHeader, ToolChip, ToolSearch, Toolbar } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchAdminSupport,
  setSupportStatus,
  type AdminSupportListParams,
  type AdminSupportRow,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminSupportListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'spam', label: 'Spam' },
];

const fmtTime = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

interface ActionCtx {
  busyId: number | null;
  setStatus: (id: number, status: 'new' | 'in_progress' | 'resolved' | 'spam') => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminSupportRow>[] => [
  {
    key: 'from',
    h: 'From',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.name?.trim() || r.email}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">{r.email}</div>
      </div>
    ),
  },
  {
    key: 'subject',
    h: 'Subject',
    cell: (r) => (
      <div className="max-w-[400px]">
        <div className="truncate font-medium">{r.subject ?? '(no subject)'}</div>
        <div className="line-clamp-1 text-[11.5px] text-[color:var(--bo-mute)]">{r.message}</div>
      </div>
    ),
  },
  { key: 'source', h: 'Source', cell: (r) => r.source ?? '—' },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'time',
    h: 'Received',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtTime(r.createdAt)}
      </span>
    ),
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busyId === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status !== 'in_progress' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.setStatus(r.id, 'in_progress')}>
              Start
            </RowAction>
          ) : null}
          {r.status !== 'resolved' ? (
            <RowAction busy={busy} onClick={() => ctx.setStatus(r.id, 'resolved')}>
              Resolve
            </RowAction>
          ) : null}
          {r.status !== 'spam' ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.setStatus(r.id, 'spam')}>
              Spam
            </RowAction>
          ) : null}
        </div>
      );
    },
  },
];

export function Support() {
  const [filter, setFilter] = useState<FilterKey>('new');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const queryClient = useQueryClient();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'support', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminSupport(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const setStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'new' | 'in_progress' | 'resolved' | 'spam' }) =>
      setSupportStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'support'] });
    },
  });

  const cols = buildColumns({
    busyId: setStatusMut.isPending && setStatusMut.variables ? setStatusMut.variables.id : null,
    setStatus: (id, status) => setStatusMut.mutate({ id, status }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? {};
  const mutationError = setStatusMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Trust"
        title="Support"
        sub={`${total.toLocaleString()} total · ${counts.new ?? 0} new · ${counts.in_progress ?? 0} in progress.`}
      />
      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load support tickets: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Status update failed: {(mutationError as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search email, subject, message…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
            {counts[f.key] != null ? (
              <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
                {counts[f.key]}
              </span>
            ) : null}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching tickets.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
